import { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  useTheme,
  keyframes,
} from "@mui/material";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import WhatsAppIcon from "@/modules/common/components/ui-kit/icons/whatsapp-icon";
import WhatsappQrModal from "@/modules/common/components/whatsapp-relink/whatsapp-qr-modal";
import useDraggable from "@/modules/common/hooks/use-draggable.hook";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(37, 211, 102, 0); }
  100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); }
`;

const WhatsappSetupPrompt = () => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const { dragRef, isDragging, hasDragged, dragHandlers, positionStyles } =
    useDraggable({ isMobile });

  if (dismissed) return null;

  return (
    <>
      <Box
        ref={dragRef}
        {...dragHandlers}
        sx={{
          position: "fixed",
          zIndex: 1300,
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          touchAction: "none",
          ...positionStyles,
        }}
      >
        {/* Collapsed Chip */}
        <Box
          onClick={() => {
            if (!hasDragged.current) setExpanded((prev) => !prev);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            padding: `${theme.nawy.spacing.xs} ${theme.nawy.spacing.sm}`,
            backgroundColor: "#DCEDE8",
            border: "1.5px solid #25D366",
            borderRadius: expanded ? "12px 12px 0 0" : "28px",
            cursor: isDragging ? "grabbing" : "pointer",
            transition: isDragging ? "none" : "all 0.3s ease",
            animation: `${pulse} 2s infinite`,
            minWidth: 180,
            "&:hover": {
              backgroundColor: "#DCEDE8",
              filter: "brightness(0.97)",
            },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: "#25D366",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WhatsAppIcon size={20} color="#fff" />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                color: theme.nawy.colors.semantic.text.primary,
                lineHeight: 1.2,
              }}
            >
              Link WhatsApp
            </Typography>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.xs?.regular ??
                  theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
                fontSize: 12,
                lineHeight: 1.2,
              }}
            >
              Setup required
            </Typography>
          </Box>

          {expanded ? (
            <ChevronDown
              size={16}
              color={theme.nawy.colors.semantic.text.secondary}
            />
          ) : (
            <ChevronUp
              size={16}
              color={theme.nawy.colors.semantic.text.secondary}
            />
          )}

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDismissed(true);
            }}
            sx={{
              color: theme.nawy.colors.semantic.text.secondary,
              padding: "2px",
            }}
            aria-label="Dismiss WhatsApp setup prompt"
          >
            <X size={14} />
          </IconButton>
        </Box>

        {/* Expanded Panel */}
        <Collapse in={expanded}>
          <Box
            sx={{
              backgroundColor: theme.nawy.colors.semantic.layer.primary,
              border: "1.5px solid #25D366",
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              padding: theme.nawy.spacing.sm,
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.xs,
              minWidth: 240,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
              }}
            >
              Link your WhatsApp Business account to start receiving customer
              messages.
            </Typography>

            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={() => setQrOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: theme.nawy.radius.sm,
                backgroundColor: "#25D366",
                color: "#fff",
                ...theme.nawy.typography.english.variants.body.sm.semibold,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#25D366",
                  filter: "brightness(0.95)",
                  boxShadow: "none",
                },
              }}
            >
              Link Now
            </Button>
          </Box>
        </Collapse>
      </Box>

      <WhatsappQrModal
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        isSetup={true}
      />
    </>
  );
};

export default WhatsappSetupPrompt;
